import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { applyValidator } from "@/lib/api";
import { inputCls, labelCls } from "@/lib/modal";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const initial = {
  full_name: "",
  email: "",
  telegram_x: "",
  country: "",
  technical_background: "",
  node_experience: "",
  cloud_experience: "",
  motivation: "",
};

const FIELDS = [
  { key: "full_name", label: "Full Name", placeholder: "Your full name" },
  { key: "email", label: "Email", placeholder: "you@example.com", type: "email" },
  { key: "telegram_x", label: "Telegram / X", placeholder: "@handle", required: false },
  { key: "country", label: "Country", placeholder: "Country of residence" },
  { key: "technical_background", label: "Technical Background", placeholder: "Engineering, infrastructure, operations...", area: true },
  { key: "node_experience", label: "Node / Infrastructure Experience", placeholder: "Verifiers, nodes, servers you have operated...", area: true },
  { key: "cloud_experience", label: "Cloud Experience", placeholder: "AWS, GCP, bare metal, Kubernetes...", area: true },
  { key: "motivation", label: "Why do you want to become a Founding Verifier?", placeholder: "Tell us what you want to build with Synaphy...", area: true },
];

export const ValidatorModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v.trim()])
      );
      const res = await applyValidator(payload);
      setDone(res);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not submit your application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(null); setForm(initial); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-lenis-prevent
        className="bg-[#0A0A0A] border-white/10 text-white sm:rounded-2xl max-h-[88vh] overflow-y-auto overscroll-contain shadow-[0_0_80px_rgba(0,240,255,0.08)]"
        data-testid="validator-modal"
      >
        {!done ? (
          <>
            <DialogHeader>
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-400 mb-2">
                Limited cohort · 100 positions
              </div>
              <DialogTitle className="font-display text-2xl font-medium tracking-tight text-white">
                Founding Verifier Application
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm leading-relaxed">
                Founding Verifier participation requirements and terms are provided
                during the application and due-diligence process. Participation is
                subject to technical, legal and protocol requirements.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 space-y-5">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label htmlFor={`va-${f.key}`} className={labelCls}>{f.label}</label>
                  {f.area ? (
                    <textarea
                      id={`va-${f.key}`}
                      data-testid={`va-${f.key.replace(/_/g, "-")}-input`}
                      required={f.required !== false}
                      rows={f.key === "motivation" ? 4 : 2}
                      value={form[f.key]}
                      onChange={set(f.key)}
                      placeholder={f.placeholder}
                      className={`${inputCls} resize-none`}
                    />
                  ) : (
                    <input
                      id={`va-${f.key}`}
                      data-testid={`va-${f.key.replace(/_/g, "-")}-input`}
                      type={f.type || "text"}
                      required={f.required !== false}
                      value={form[f.key]}
                      onChange={set(f.key)}
                      placeholder={f.placeholder}
                      className={inputCls}
                    />
                  )}
                </div>
              ))}
              <button
                type="submit"
                data-testid="validator-submit-btn"
                disabled={loading}
                className="w-full rounded-full bg-cyan-400 py-3.5 font-display text-sm font-semibold tracking-wider text-black transition-[background-color,box-shadow] duration-300 hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.35)] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                APPLY FOR FOUNDING VERIFIER
              </button>
              <p className="text-center font-mono text-[10px] tracking-[0.15em] text-zinc-600 leading-relaxed">
                Synaphy will never ask for passwords, payment details or any sensitive credentials.
              </p>
            </form>
          </>
        ) : (
          <div className="py-6 text-left" data-testid="validator-success">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-400/[0.06]">
              <ShieldCheck className="h-6 w-6 text-cyan-400" />
            </div>
            <h3 className="font-display text-3xl font-medium tracking-tight text-white text-center">
              Application received.
            </h3>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed text-center">
              Our team will review your profile and contact shortlisted candidates.
            </p>
            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-5 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Application reference
              </div>
              <div className="font-mono text-lg tracking-[0.15em] text-cyan-400" data-testid="validator-application-id">
                {done.application_id}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
